/**
 * src/hooks/useWCProducts.ts
 *
 * Fetches WooCommerce service products and item products from WordPress,
 * and merges them into the ServiceConfig / BookingItemData shape that the
 * existing React booking components already consume.
 *
 * Falls back to the static mock data when the API is unavailable, so the
 * app still works during development without a live WP backend.
 */

import { useEffect, useState } from 'react';
import { productsApi, type WCProduct, type WCLoadSize } from '../lib/api';
import {
  bookingServices,
  type ServiceConfig,
  type BookingItemData,
} from '../mocks/bookingData';
import { loadSizes as mockLoadSizes, type LoadSize } from '../mocks/calculatorData';

// ── Service products ──────────────────────────────────────────────────────────
export function useWCServices() {
  const [services, setServices] = useState<ServiceConfig[]>(bookingServices);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    productsApi
      .getServices()
      .then((wcProducts) => {
        const merged = mergeServices(wcProducts, bookingServices);
        setServices(merged);
      })
      .catch((err) => {
        console.warn('[Cleario] WC services fetch failed, using mock data.', err);
        setError(String(err?.message));
      })
      .finally(() => setLoading(false));
  }, []);

  return { services, loading, error };
}

// ── Item products (per-item booking) ──────────────────────────────────────────
export function useWCItems(serviceSlug: string) {
  const [items, setItems] = useState<BookingItemData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    productsApi
      .getBySlug(serviceSlug)
      .then((product) => {
        if (product.type === 'variable' && product.variations) {
          // Map variations → BookingItemData for item-style services
          const mapped: BookingItemData[] = product.variations.map((v) => ({
            id:        v.cleario_var_id,
            label:     v.label,
            unitPrice: v.price,
            icon:      'ri-price-tag-3-line',
            category:  'service',
          }));
          setItems(mapped);
        }
      })
      .catch(() => {
        // Fallback handled by caller
      })
      .finally(() => setLoading(false));
  }, [serviceSlug]);

  return { items, loading };
}

// ── All item products (for Step3Items, etc.) ──────────────────────────────────
export function useWCAllItems() {
  const [items, setItems]   = useState<BookingItemData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    productsApi
      .getItems()
      .then((wcProducts) => {
        const mapped: BookingItemData[] = wcProducts.map((p) => ({
          id:        p.slug,
          label:     p.name,
          unitPrice: parseFloat(p.price),
          icon:      'ri-price-tag-3-line',
          category:  'item',
        }));
        setItems(mapped);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return { items, loading };
}

// ── Load sizes (calculator) ────────────────────────────────────────────────────
export function useWCLoadSizes() {
  const [loadSizes, setLoadSizes] = useState<LoadSize[]>(mockLoadSizes);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    productsApi
      .getLoadSizes()
      .then((wcSizes: WCLoadSize[]) => {
        const mapped: LoadSize[] = wcSizes.map((s, i) => {
          const mock = mockLoadSizes[i] ?? mockLoadSizes[mockLoadSizes.length - 1];
          return {
            id:          s.id,
            label:       s.label,
            minYards:    s.volume_yd3 > 0 ? Math.max(0, s.volume_yd3 - 1) : 0,
            maxYards:    s.volume_yd3,
            price:       s.price,
            description: mock.description,
            icon:        mock.icon,
            popular:     mock.popular,
          };
        });
        if (mapped.length) setLoadSizes(mapped);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return { loadSizes, loading };
}

// ── Merge helper: apply WC prices onto the static mock service configs ─────────
function mergeServices(wcProducts: WCProduct[], mocks: ServiceConfig[]): ServiceConfig[] {
  return mocks.map((mock) => {
    const wc = wcProducts.find((p) => p.slug === mock.slug);
    if (!wc) return mock;

    // Overlay prices from WC onto the options array
    const updatedOptions = mock.options.map((opt) => {
      const wcVar = wc.variations?.find(
        (v) => v.cleario_var_id === opt.id || v.label.toLowerCase() === opt.label.toLowerCase()
      );
      if (!wcVar) return opt;
      return {
        ...opt,
        basePrice:  wcVar.price,
        priceLabel: `£${wcVar.price.toFixed(2)}`,
      };
    });

    // Update priceFrom
    const minPrice = updatedOptions.reduce(
      (min, o) => Math.min(min, o.basePrice),
      Infinity
    );

    return {
      ...mock,
      options:   updatedOptions,
      minPrice:  isFinite(minPrice) ? minPrice : mock.minPrice,
      priceFrom: isFinite(minPrice) ? `from £${minPrice.toFixed(2)}` : mock.priceFrom,
    };
  });
}
